import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

export interface AddressFromCoordinatesResponse {
  address: string;
  zipCode: string;
}

export interface GeocodeAddressMatch {
  formattedAddress: string;
  latitude: number;
  longitude: number;
  zipCode: string;
}

/**
 * `match`: Google returned a usable result.
 * `not_found`: Google responded successfully but had nothing for that query.
 * `error`: HTTP failure — caller should treat the geocoder as unavailable.
 */
export type GeocodeAddressResult =
  | { kind: 'match'; result: GeocodeAddressMatch }
  | { kind: 'not_found' }
  | { kind: 'error' };

interface GoogleMapsAddressComponent {
  short_name: string;
  long_name: string;
  types: string[];
}

interface GoogleMapsGeometry {
  location: {
    lat: number;
    lng: number;
  };
}

interface GoogleMapsResult {
  formatted_address: string;
  address_components: GoogleMapsAddressComponent[];
  geometry?: GoogleMapsGeometry;
  partial_match?: boolean;
  types?: string[];
}

/**
 * Result `types` values that indicate Google fell back to a region (country,
 * state, county, etc.) instead of an actual address. We reject these because
 * they're too imprecise to be useful as a search anchor — e.g. an unmatched
 * "zzz xxx vv" query with `components=country:US` returns the geographic
 * center of the United States with `types: ["country", "political"]`.
 */
const TOO_BROAD_RESULT_TYPES = new Set([
  'country',
  'administrative_area_level_1', // state
  'administrative_area_level_2', // county
]);

interface GoogleMapsGeocodingResponse {
  results: GoogleMapsResult[];
  status: string;
}

/**
 * Service providing Google Maps Geocoding API functionality
 * Uses REST API with API key authentication
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsApiService {
  private readonly GEOCODING_URL =
    'https://maps.googleapis.com/maps/api/geocode/json';
  private readonly LAT_LNG_DIGITS = 6;

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentService
  ) {}

  /**
   * Returns a free-form address from latitude/longitude coordinates
   * using Google Maps Geocoding REST API
   *
   * @see https://developers.google.com/maps/documentation/geocoding/requests-reverse-geocoding
   */
  getAddressFromCoordinates(
    latitude: number,
    longitude: number
  ): Observable<AddressFromCoordinatesResponse> {
    const url = new URL(this.GEOCODING_URL);

    // Add the API key
    const mapsKey = this.environmentService.googleMapsKey;
    if (mapsKey) {
      url.searchParams.set('key', atob(mapsKey));
    }

    // Add coordinates and language
    url.searchParams.set(
      'latlng',
      `${latitude.toFixed(this.LAT_LNG_DIGITS)},${longitude.toFixed(
        this.LAT_LNG_DIGITS
      )}`
    );
    url.searchParams.set('language', 'en');

    return this.http.get<GoogleMapsGeocodingResponse>(url.toString()).pipe(
      map((response) => this.transformResponse(response)),
      catchError(() => of({ address: 'Current Location', zipCode: '' }))
    );
  }

  /**
   * Forward-geocodes a free-form address string to coordinates using the
   * Google Maps Geocoding REST API. Used as a fallback when SmartyStreets
   * and Algolia return no suggestions.
   *
   * @see https://developers.google.com/maps/documentation/geocoding/requests-geocoding
   */
  geocodeAddress(address: string): Observable<GeocodeAddressResult> {
    const url = new URL(this.GEOCODING_URL);

    const mapsKey = this.environmentService.googleMapsKey;
    if (mapsKey) {
      url.searchParams.set('key', atob(mapsKey));
    }

    url.searchParams.set('address', address);
    url.searchParams.set('language', 'en');
    // Restrict results to the United States. Google's `components` filter
    // is a hard restriction (vs. `region` which is only a bias hint), so
    // queries like "10001" won't return matches in other countries.
    // https://developers.google.com/maps/documentation/geocoding/requests-geocoding#component-filtering
    url.searchParams.set('components', 'country:US');

    return this.http.get<GoogleMapsGeocodingResponse>(url.toString()).pipe(
      map((response) => this.transformGeocodeResponse(response)),
      catchError(() => of<GeocodeAddressResult>({ kind: 'error' }))
    );
  }

  private transformResponse(
    response: GoogleMapsGeocodingResponse
  ): AddressFromCoordinatesResponse {
    if (!response.results || response.results.length === 0) {
      return { address: 'Current Location', zipCode: '' };
    }

    // Remove ", USA" suffix since we assume the user's in the US
    const address = response.results[0].formatted_address.replace(/, USA$/, '');

    // Extract ZIP code from address components
    const zipCode =
      response.results[0].address_components.find((component) =>
        component.types.includes('postal_code')
      )?.short_name ?? '';

    return { address, zipCode };
  }

  private transformGeocodeResponse(
    response: GoogleMapsGeocodingResponse
  ): GeocodeAddressResult {
    if (!response.results || response.results.length === 0) {
      return { kind: 'not_found' };
    }

    const result = response.results[0];
    const location = result.geometry?.location;

    if (!location) {
      return { kind: 'not_found' };
    }

    // Google often falls back to the geographic center of a country, state,
    // or county for unmatched queries (especially with `components=country:US`).
    // Treat those as "not found" — anchoring a lab search to the centroid of
    // the entire US, or even of a state, would be misleading to the user.
    const resultTypes = result.types ?? [];
    const isTooBroad = resultTypes.some((t) => TOO_BROAD_RESULT_TYPES.has(t));
    if (isTooBroad) {
      return { kind: 'not_found' };
    }

    const formattedAddress = result.formatted_address.replace(/, USA$/, '');
    const zipCode =
      result.address_components.find((component) =>
        component.types.includes('postal_code')
      )?.short_name ?? '';

    return {
      kind: 'match',
      result: {
        formattedAddress,
        latitude: location.lat,
        longitude: location.lng,
        zipCode,
      },
    };
  }
}
